import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, TrendingDown, DollarSign, Calendar, ArrowRightLeft, RefreshCw, Clock, Target, BarChart3, ArrowLeft, Info } from "lucide-react";
import Breadcrumb from './Breadcrumb';

const SIPDashboard = ({ client, onRefresh, onBack, onNavigate }) => {
  const [sipSummary, setSipSummary] = useState(null);
  const [sipDetails, setSipDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Mobile detection hook
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch SIP data on component mount
  useEffect(() => {
    fetchSIPData();
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      const mainContent = document.querySelector('main .overflow-auto');
      if (mainContent) {
        mainContent.scrollTop = 0;
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };
    
    setTimeout(scrollToTop, 0);
    setTimeout(scrollToTop, 50);
    setTimeout(scrollToTop, 150);
  }, []);

  const fetchSIPData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      // Fetch SIP summary
      const summaryResponse = await fetch(`${API_URL}/api/client/sip-summary`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      // Fetch SIP details
      const detailsResponse = await fetch(`${API_URL}/api/client/sip-details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        console.log('✅ SIP Summary data:', summaryData);
        setSipSummary(summaryData);
      } else {
        console.log('❌ SIP Summary API failed');
        setSipSummary(null);
      }

      if (detailsResponse.ok) {
        const detailsData = await detailsResponse.json();
        console.log('✅ SIP Details data:', detailsData);
        setSipDetails(detailsData || []);
      } else {
        console.log('❌ SIP Details API failed');
        setSipDetails([]);
      }

    } catch (error) {
      console.error('Error fetching SIP data:', error);
      setError(error.message);
      setSipSummary(null);
      setSipDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchSIPData();
    if (onRefresh) {
      await onRefresh();
    }
    setIsRefreshing(false);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
    
    // Also use navigation handler if available
    if (onNavigate) {
      onNavigate('portfolio');
    }
    
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      const mainContent = document.querySelector('main .overflow-auto');
      if (mainContent) {
        mainContent.scrollTop = 0;
        mainContent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 50);
  };

  // Breadcrumb configuration
  const getBreadcrumbItems = () => {
    return [
      {
        label: 'Portfolio',
        href: 'portfolio'
      },
      {
        label: 'SIP Dashboard',
        href: null // Current page, not clickable
      }
    ];
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbNavigate = (href) => {
    switch (href) {
      case 'dashboard':
        if (onNavigate) {
          onNavigate('dashboard');
        }
        break;
      case 'portfolio':
        handleBack(); // Use the same logic as back button
        break;
      default:
        break;
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format percentage - Fix to handle the ROI correctly
  const formatPercentage = (percent) => {
    if (percent === null || percent === undefined || isNaN(percent)) return '0.00%';
    const sign = percent >= 0 ? '+' : '';
    return `${sign}${percent.toFixed(2)}%`;
  };

  // Get trend color
  const getTrendColor = (value) => {
    if (value > 0) return 'text-green-400';
    if (value < 0) return 'text-red-400';
    return 'text-[#B8B8B8]';
  };

  // Mobile-responsive Metric Card Component with tooltips
  const MetricCard = ({ title, value, subtitle, icon: Icon, trend, isNegative = false, tooltip }) => {
    const [showTooltip, setShowTooltip] = useState(false);
    const tooltipRef = useRef(null);

    useEffect(() => {
      if (!showTooltip) return;

      const handleClickOutside = (event) => {
        if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
          setShowTooltip(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }, [showTooltip]);

    return (
      <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6 relative overflow-hidden group hover:border-[#676DFF]/30 transition-all duration-300">
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#676DFF] to-[#5A60E8]"></div>
        
        <div className="flex justify-between items-start mb-3 sm:mb-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#0A0A0A] rounded-lg flex items-center justify-center">
              <Icon className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
            </div>
            <div className="flex items-center space-x-1 sm:space-x-2">
              <h3 className="text-xs sm:text-sm text-[#B8B8B8] font-medium tracking-wide uppercase" style={{fontFamily: 'Lexend, sans-serif'}}>
                {title}
              </h3>
              {tooltip && (
                <div className="relative" ref={tooltipRef}>
                  <Info 
                    className="h-3 w-3 sm:h-4 sm:w-4 text-[#B8B8B8] hover:text-[#676DFF] cursor-help transition-colors"
                    onClick={() => setShowTooltip(prev => !prev)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      setShowTooltip(prev => !prev);
                    }}
                  />
                  {showTooltip && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-[#0A0A0A]/95 backdrop-blur border border-[#676DFF]/30 rounded-lg text-xs text-white z-50 shadow-xl min-w-max">
                      <div className="space-y-1">
                        {tooltip.map((line, index) => (
                          <div key={index} className="text-left">{line}</div>
                        ))}
                      </div>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-[#676DFF]/30"></div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className={`text-xl sm:text-2xl font-bold mb-2 tracking-tight ${isNegative ? 'text-red-400' : 'text-white'}`} style={{fontFamily: 'Lexend, sans-serif'}}>
          {value}
        </div>
        
        {trend && (
          <div className="flex items-center space-x-2">
            <div className={`flex items-center space-x-1 text-xs sm:text-sm font-medium ${getTrendColor(parseFloat(trend.replace(/[^-0-9.]/g, '')))}`}>
              {trend.includes('+') ? (
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
              ) : trend.includes('-') ? (
                <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4" />
              ) : null}
              <span>{trend}</span>
            </div>
          </div>
        )}
        
        {subtitle && (
          <p className="text-xs text-[#808080] mt-2" style={{fontFamily: 'Lexend, sans-serif'}}>
            {subtitle}
          </p>
        )}
      </div>
    );
  };


  const SIPPlanCard = ({ plan, index }) => {
  return (
    <div className="bg-[#151515]/80 backdrop-blur border border-[#676DFF]/20 rounded-2xl p-4 sm:p-6 lg:p-8 relative overflow-hidden group hover:border-[#676DFF]/30 transition-all duration-300">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#676DFF] to-[#5A60E8]"></div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3 sm:space-x-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#676DFF]/20 rounded-xl flex items-center justify-center">
            <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-[#676DFF]" />
          </div>
          <div>
            <h3 className="text-lg sm:text-xl font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              {plan.assetOut} SIP Plan
            </h3>
            <p className="text-sm sm:text-base text-[#B8B8B8]" style={{fontFamily: 'Lexend, sans-serif'}}>
              {plan.assetIn} → {plan.assetOut}
            </p>
          </div>
        </div>
        <div className="text-left sm:text-right">
          <div className={`text-base sm:text-lg font-semibold ${getTrendColor(plan.roi)}`} style={{fontFamily: 'Lexend, sans-serif'}}>
            {formatPercentage(plan.roi)}
          </div>
          <p className="text-xs sm:text-sm text-[#808080]">ROI</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
        <div className="space-y-4 sm:space-y-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
              <p className="text-xs sm:text-sm text-[#B8B8B8] uppercase tracking-wider font-medium" style={{fontFamily: 'Lexend, sans-serif'}}>Frequency</p>
            </div>
            <p className="text-sm sm:text-base font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              {plan.frequency}
            </p>
          </div>
          
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
              <p className="text-xs sm:text-sm text-[#B8B8B8] uppercase tracking-wider font-medium" style={{fontFamily: 'Lexend, sans-serif'}}>
                Total Invested
              </p>
            </div>
            <p className="text-sm sm:text-base font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              {formatCurrency(plan.totalInvested)}
            </p>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Target className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
              <p className="text-xs sm:text-sm text-[#B8B8B8] uppercase tracking-wider font-medium" style={{fontFamily: 'Lexend, sans-serif'}}>Amount per Investment</p>
            </div>
            <p className="text-sm sm:text-base font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              {formatCurrency(plan.amount)}
            </p>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <ArrowRightLeft className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
              <p className="text-xs sm:text-sm text-[#B8B8B8] uppercase tracking-wider font-medium" style={{fontFamily: 'Lexend, sans-serif'}}>Exchange</p>
            </div>
            <p className="text-sm sm:text-base font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              {plan.assetIn} → {plan.assetOut}
            </p>
          </div>
          
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
              <p className="text-xs sm:text-sm text-[#B8B8B8] uppercase tracking-wider font-medium" style={{fontFamily: 'Lexend, sans-serif'}}>Current Value</p>
            </div>
            <p className="text-sm sm:text-base font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              {formatCurrency(plan.currentValue)}
            </p>
          </div>

          <div>
            <div className="flex items-center space-x-2 mb-2">
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4 text-[#676DFF]" />
              <p className="text-xs sm:text-sm text-[#B8B8B8] uppercase tracking-wider font-medium" style={{fontFamily: 'Lexend, sans-serif'}}>Status</p>
            </div>
            <p className="text-sm sm:text-base font-semibold text-white" style={{fontFamily: 'Lexend, sans-serif'}}>
              {plan.status ? 'Active' : 'Inactive'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

  if (isLoading) {
    return (
      <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
        {/* Breadcrumb */}
        <Breadcrumb 
          items={getBreadcrumbItems()} 
          onNavigate={handleBreadcrumbNavigate}
        />
        
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-6 h-6 sm:w-8 sm:h-8 border-2 border-[#676DFF] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-[#808080] text-sm sm:text-base">Loading SIP data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
        {/* Breadcrumb */}
        <Breadcrumb 
          items={getBreadcrumbItems()} 
          onNavigate={handleBreadcrumbNavigate}
        />
        
        <div className="text-center py-12 sm:py-16">
          <div className="text-red-400 font-semibold mb-2 text-sm sm:text-base">Error</div>
          <div className="text-[#E0E0E0] text-sm sm:text-base mb-6">{error}</div>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-[#676DFF] hover:bg-[#5A60E8] text-white rounded-lg transition-colors text-sm font-medium"
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!sipSummary || (!sipDetails || sipDetails.length === 0)) {
    return (
      <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
        {/* Breadcrumb */}
        <Breadcrumb 
          items={getBreadcrumbItems()} 
          onNavigate={handleBreadcrumbNavigate}
        />
        
        <div className="text-center py-12 sm:py-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#676DFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-[#676DFF] opacity-60" />
          </div>
          <h3 className="text-lg sm:text-xl font-semibold text-white mb-3">No SIP Data Available</h3>
          <p className="text-[#B8B8B8] leading-relaxed text-sm sm:text-base px-4 mb-6">
            Contact your advisor to start your Systematic Investment Plan.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-[#676DFF]/10 hover:bg-[#676DFF]/20 border border-[#676DFF]/30 hover:border-[#676DFF]/50 text-[#676DFF] hover:text-white rounded-lg transition-all duration-200 text-sm font-medium flex items-center space-x-2"
            style={{fontFamily: 'Lexend, sans-serif'}}
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Portfolio</span>
          </button>
        </div>
      </div>
    );
  }

  // Create tooltip content for total invested
  const totalInvestedTooltip = [
    `Total Invested: ${formatCurrency(sipSummary.totalInvested || 0)}`, // This should be $617 from total_invested field
    `Total Withdrawn: ${formatCurrency(sipSummary.totalWithdrawn || 0)}`, // This should be $0 from total_withdrawn field
    `Idle Amount: ${formatCurrency(sipSummary.idle || 0)}` // This should be $109.33 from idle field
  ];

 return (
    <div className="space-y-6 sm:space-y-8 px-4 sm:px-0" style={{fontFamily: 'Lexend, sans-serif'}}>
      {/* Breadcrumb */}
      <Breadcrumb 
        items={getBreadcrumbItems()} 
        onNavigate={handleBreadcrumbNavigate}
      />

      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>

          <p className="text-[#B8B8B8] text-xs sm:text-sm" style={{fontFamily: 'Lexend, sans-serif'}}>
            Track your automated crypto investment plans and performance
          </p>
        </div>
      </div>

      {/* SIP Summary Cards - ONLY add idle here */}
      <div>
        <h2 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6" style={{fontFamily: 'Lexend, sans-serif'}}>
          Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <MetricCard
            title="Total Invested"
            value={formatCurrency((sipSummary.totalInvested || 0) + (sipSummary.idle || 0))}
            icon={DollarSign}
            tooltip={totalInvestedTooltip}
          />
          <MetricCard
            title="Current Value"
            value={formatCurrency(sipSummary.currentValue)}
            icon={TrendingUp}
          />
          <MetricCard
            title="Return on Investment"
            value={formatPercentage(sipSummary.roi)}
            icon={sipSummary.roi >= 0 ? TrendingUp : TrendingDown}
            trend={formatPercentage(sipSummary.roi)}
            isNegative={sipSummary.roi < 0}
          />
        </div>
      </div>

      {/* SIP Plans Section - Individual plans show ONLY their data */}
      {sipDetails && sipDetails.length > 0 && (
        <div>
          <h2 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6" style={{fontFamily: 'Lexend, sans-serif'}}>
            Plan Details
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {sipDetails.map((plan, index) => (
              <SIPPlanCard key={`sip-plan-${index}`} plan={plan} index={index} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SIPDashboard;